import React from 'react';
import styled from 'styled-components';
import i18next from 'i18next';
import leftArrow from './left-arrow.svg';
import { colors } from '../../../styles/colors';

const ITEMS_PER_PAGE = 15;
const PAGE_NUMBERS_TO_SHOW = 3;
const THREE_DOTS = -1;

type Props = {
  currentPage: number;
  numRecords: number;
  onChange: (pageNumber: number, itemsPerPage: number) => void;
  setPage: (pageNumber: number) => void;
};

export class Paginator extends React.PureComponent<Props> {
  render() {
    const { currentPage, numRecords } = this.props;

    const numPages = Math.ceil(numRecords / ITEMS_PER_PAGE) || 1;

    return <PaginatorContainer className="Paginator">
      <TotalRecordsLabel>
        {/* {numRecords.toLocaleString()} {i18next.t('cmp.table.footer.text')} */}
      </TotalRecordsLabel>
      <PageNumbersContainer>
        <PrevButton type="button" disabled={currentPage === 1} onClick={this.prev}>
          {i18next.t('table.footer.previousButton')}
        </PrevButton>
        <span>
          {
            generateDisplayedPageNumbers(currentPage, numPages).map((n, i) =>
              n === THREE_DOTS
                ? <ThreeDots key={n + '...' + i}>&hellip;</ThreeDots>
                : <PageNumber onClick={() => this.goTo(n)} className={currentPage === n ? 'active' : ''} key={n}>
                  {n}
                </PageNumber>)
          }
        </span>
        <NextButton type="button" disabled={currentPage === numPages} onClick={this.next}>
          {i18next.t('table.footer.nextButton')}
        </NextButton>
      </PageNumbersContainer>
    </PaginatorContainer>;
  }

  prev = () => this.goTo(this.props.currentPage - 1);
  next = () => this.goTo(this.props.currentPage + 1);

  goTo = (page: number) => {
    this.props.setPage(page);
    this.props.onChange(page, ITEMS_PER_PAGE);
  }
}

const PaginatorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 0.9em;
  user-select: none;
`;

const PageNumbersContainer = styled.div`
  border-radius: 0.5rem;
  background: white;
  padding: 0.5rem 1.5rem;
`;

const PrevButton = styled.button`
  /* Reset default styles */
  border: none;
  background: none;
  font-size: 0.95em;
  font-family: 'Open Sans', sans-serif;

  padding-left: 1rem;
  line-height: 1.5rem;
  cursor: pointer;
  position: relative;
  color: ${colors.text};
  transition: color 0.25s;

  ::before {
    content: '';
    position: absolute;
    left: 0;
    background: url(${leftArrow}) left center no-repeat;
    width: 1rem;
    height: 1.5rem;
    filter: grayscale(100) saturate(100) contrast(100); /* darken image */
  }

  &:hover {
    color: ${colors.textLight};

    ::before {
      filter: none;
    }
  }

  :focus {
    box-shadow: none;
    outline: 1px solid ${colors.borderLight};
  }

  :disabled {
    cursor: not-allowed;
    opacity: 0.25;

    &:hover {
      color: ${colors.text};
    }
  }
`;

const NextButton = styled(PrevButton)`
  padding-left: unset;
  padding-right: 1rem;

  ::before {
    left: unset;
    right: 0;
    transform: rotate(180deg);
  }
`;

const PageNumber = styled.div`
  display: inline-block;
  height: calc(1.5rem + 3px);
  padding: 0 0.5em;
  line-height: 1.5rem;
  margin: 0 0.25em;
  font-size: 0.95em;
  color: ${colors.text};
  text-align: center;
  cursor: pointer;
  transition: color 0.25s, box-shadow 0.25s;
  box-shadow: inset -50px 0 0 0 white, inset 50px 0 0 0 white, inset 0px -3px 0 0 ${colors.circleBlue};

  &:first-child {
    margin-left: 1rem;
  }

  &:last-child {
    margin-right: 1rem;
  }

  &:hover:not(.active) {
    color: ${colors.circleBlue};
    box-shadow: inset 0 0 0 0 white, inset 0 0 0 0 white, inset 0px -3px 0 0 ${colors.circleBlue}33;
  }

  &.active {
    color: ${colors.circleBlue};
    font-weight: bold;
    box-shadow: inset 0 0 0 0 white, inset 0 0 0 0 white, inset 0px -3px 0 0 ${colors.circleBlue};
  }
`;

const ThreeDots = styled.div`
  display: inline-block;
`;

const TotalRecordsLabel = styled.div`
  margin: 0 2em;
`;

function generateDisplayedPageNumbers(currentPage: number, totalPages: number): number[] {
  const requiredArraySize = Math.min(PAGE_NUMBERS_TO_SHOW, totalPages);

  const pageNumbers = [currentPage];
  let i = 0;
  while (pageNumbers.length < requiredArraySize) {
    // Alternate between adding numbers at the beginning and at the end:
    if (i % 2 === 0) {
      const nextNumber = pageNumbers[0] - 1;
      if (nextNumber >= 1) {
        pageNumbers.unshift(nextNumber);
      }
    } else {
      const nextNumber = pageNumbers[pageNumbers.length - 1] + 1;
      if (nextNumber <= totalPages) {
        pageNumbers.push(nextNumber);
      }
    }

    i++;
  }

  if (!pageNumbers.includes(1) && !pageNumbers.includes(2) && totalPages > requiredArraySize) {
    pageNumbers.unshift(THREE_DOTS);
    pageNumbers.unshift(1);
  }

  if (!pageNumbers.includes(totalPages) && !pageNumbers.includes(totalPages - 1) && totalPages > requiredArraySize) {
    pageNumbers.push(THREE_DOTS);
    pageNumbers.push(totalPages);
  }

  return pageNumbers;
}
